/**
 * One-line human summaries of each config section.
 *
 * These are what the device page and the family-defaults page show above the
 * fold, so a parent can read what a device is doing without expanding
 * anything. Shared between both screens so the two can't describe the same
 * value differently.
 */

import {
  WEEKDAYS,
  type Blocklist,
  type Cadence,
  type HarmsTaxonomy,
  type TimeWindow,
  type WeeklySchedule,
} from '../data/config';

const DAY_SHORT: Record<keyof WeeklySchedule, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

/** `HH:MM:SS` or `HH:MM` -> `HH:MM`. */
function hhmm(t: string): string {
  const parts = t.split(':');
  return `${parts[0] ?? '00'}:${parts[1] ?? '00'}`;
}

function windowsKey(windows: TimeWindow[]): string {
  if (windows.length === 0) return 'off';
  return windows.map((w) => `${hhmm(w.from)}–${hhmm(w.to)}`).join(', ');
}

/**
 * Compact weekly schedule, collapsing runs of identical days.
 *
 * "Mon–Sun · 07:30–22:00" when every day matches; "Mon–Fri · 07:00–21:00 ·
 * Sat–Sun · 08:00–22:30" when they don't. Runs are consecutive-only, so an
 * odd Wednesday splits the range rather than being silently folded in —
 * misreporting a schedule is worse than a longer string.
 */
export function summarizeSchedule(schedule: WeeklySchedule): string {
  const keys = WEEKDAYS.map((d) => windowsKey(schedule[d]));
  if (keys.every((k) => k === 'off')) return 'No capture windows set';

  const runs: { start: number; end: number; key: string }[] = [];
  for (let i = 0; i < WEEKDAYS.length; i += 1) {
    const key = keys[i] ?? 'off';
    const last = runs[runs.length - 1];
    if (last !== undefined && last.key === key) {
      last.end = i;
    } else {
      runs.push({ start: i, end: i, key });
    }
  }

  return runs
    .map((run) => {
      const startDay = WEEKDAYS[run.start];
      const endDay = WEEKDAYS[run.end];
      if (startDay === undefined || endDay === undefined) return '';
      const days =
        run.start === run.end
          ? DAY_SHORT[startDay]
          : `${DAY_SHORT[startDay]}–${DAY_SHORT[endDay]}`;
      return run.key === 'off' ? `${days} off` : `${days} ${run.key}`;
    })
    .filter((s) => s.length > 0)
    .join(' · ');
}

/** "Every 10 minutes" / "Every 45 seconds" / "Every 2 hours". */
export function summarizeCadence(cadence: Cadence): string {
  const s = cadence.active_seconds;
  if (s < 60) return `Every ${String(s)} seconds`;
  if (s < 3600) {
    const m = Math.round(s / 60);
    return `Every ${String(m)} minute${m === 1 ? '' : 's'}`;
  }
  const h = Math.round((s / 3600) * 10) / 10;
  return `Every ${String(h)} hour${h === 1 ? '' : 's'}`;
}

/** "5 categories" plus the labels, for the detail line. */
export function summarizeTaxonomy(taxonomy: HarmsTaxonomy): string {
  const n = taxonomy.categories.length;
  if (n === 0) return 'No categories — nothing will be flagged';
  return `${String(n)} categor${n === 1 ? 'y' : 'ies'}`;
}

/** Comma-joined category labels, truncated for a one-line detail. */
export function taxonomyLabels(taxonomy: HarmsTaxonomy, max = 5): string {
  const labels = taxonomy.categories.map((c) => c.label);
  if (labels.length <= max) return labels.join(' · ');
  return `${labels.slice(0, max).join(' · ')} + ${String(labels.length - max)} more`;
}

/** Total domain entries across every list. */
export function countBlocklistDomains(blocklists: Blocklist[]): number {
  return blocklists.reduce((n, bl) => n + bl.entries.length, 0);
}

/** "14 domains" / "No domains blocked". */
export function summarizeBlocklists(blocklists: Blocklist[]): string {
  const n = countBlocklistDomains(blocklists);
  if (n === 0) return 'No domains blocked';
  return `${String(n)} domain${n === 1 ? '' : 's'}`;
}

/** "7 days". */
export function summarizeRetention(days: number): string {
  return `${String(days)} day${days === 1 ? '' : 's'}`;
}
